import { apiClient } from "@alaffia-technology-solutions/client-sdk-react-query";

/**
 * Supported flag value types. Mirrors the server-side resolver's coercion set.
 */
export type FeatureFlagDynamicType = "boolean" | "string" | "number" | "json";

interface UseFeatureFlagDynamicArgs<
  TType extends FeatureFlagDynamicType,
  TValue,
> {
  key: string;
  type: TType;
  defaultValue: TValue;
}

interface UseFeatureFlagDynamicResult<TValue> {
  /** The flag value resolved by the server. Falls back to `defaultValue` while loading or when unset. */
  value: TValue;
  /** True when the resolver returned `defaultValue` (flag unset / unknown). */
  isDefault: boolean;
  isLoading: boolean;
}

/**
 * Resolve a single feature flag dynamically by key.
 *
 * The flag value is fetched from the server via `featureFlagDynamic` and is
 * coerced to `TValue` based on `type`. While loading, `value` is `defaultValue`
 * and `isDefault` is `true`.
 *
 * @example
 *   const { value: showDemo, isLoading } = useFeatureFlagDynamic({
 *     key: "create_agent_demo",
 *     type: "boolean",
 *     defaultValue: false,
 *   });
 */
export function useFeatureFlagDynamic(
  args: UseFeatureFlagDynamicArgs<"boolean", boolean>,
): UseFeatureFlagDynamicResult<boolean>;
export function useFeatureFlagDynamic(
  args: UseFeatureFlagDynamicArgs<"string", string>,
): UseFeatureFlagDynamicResult<string>;
export function useFeatureFlagDynamic(
  args: UseFeatureFlagDynamicArgs<"number", number>,
): UseFeatureFlagDynamicResult<number>;
export function useFeatureFlagDynamic<TValue = unknown>(
  args: UseFeatureFlagDynamicArgs<"json", TValue>,
): UseFeatureFlagDynamicResult<TValue>;
export function useFeatureFlagDynamic<TValue>({
  key,
  type,
  defaultValue,
}: UseFeatureFlagDynamicArgs<
  FeatureFlagDynamicType,
  TValue
>): UseFeatureFlagDynamicResult<TValue> {
  const { data, isLoading } = apiClient.api.featureFlagDynamic.useQuery({
    key,
    type,
    defaultValue,
  });

  if (!data) {
    return { value: defaultValue, isDefault: true, isLoading };
  }

  return {
    value: (data.resolvedValue ?? defaultValue) as TValue,
    isDefault: data.isDefault,
    isLoading,
  };
}
